import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import PageLayout from "../../components/PageLayout";
import Toast from "../../components/Toast";
import { GET_CXO_SETTLEMENT_DASHBOARD_URL, getData } from "../../utils/api";
import { canAccessSettlementInsights, getLoginDetails } from "../../utils/storage";
import InsightsHeaderIcon from "../../icons/newicons/insights.svg";
import { InsightsSubNav } from "./InsightsSubNav";

import "./insights.css";
import "./cxo-settlement-dashboard.css";

const METAL_LABELS = { pt: "Platinum", pd: "Palladium", rh: "Rhodium" };

function money(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function moneySigned(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  const abs = Math.abs(v).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
  if (Math.abs(v) < 0.5) return "$0";
  return v > 0 ? `+${abs}` : `−${abs}`;
}

function oz(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toFixed(2);
}

function ozSigned(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  const abs = Math.abs(v).toFixed(2);
  if (Math.abs(v) < 0.00001) return "0.00";
  return v > 0 ? `+${abs}` : `−${abs}`;
}

function varClass(n) {
  if (n == null || Number.isNaN(Number(n)) || Math.abs(Number(n)) < 0.00001) return "";
  return Number(n) > 0 ? " is-pos" : " is-neg";
}

function recoveryWidth(pct) {
  const v = Number(pct);
  if (!Number.isFinite(v) || v <= 0) return 0;
  return Math.min(100, Math.max(0, v));
}

const CxoSettlementDashboard = () => {
  const loginDetails = getLoginDetails();
  const canView = canAccessSettlementInsights(loginDetails);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [toastParams, setToastParams] = useState({ message: "", type: "", id: 0 });

  useEffect(() => {
    if (!canView) window.location.href = "/";
  }, [canView]);

  const loadDashboard = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await getData(GET_CXO_SETTLEMENT_DASHBOARD_URL, {});
      setData(res?.data || res || null);
    } catch (e) {
      setData(null);
      setToastParams({
        type: "ERROR",
        message: e && e.error ? e.error : "Failed to load CXO dashboard",
        id: Date.now(),
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canView) loadDashboard();
  }, [canView, loadDashboard]);

  if (!canView) return null;

  const exec = data?.executive || {};
  const metals = data?.metals || {};
  const byOem = data?.byOem || [];
  const topPaymentLeaks = data?.topPaymentLeaks || [];
  const topOzLeaks = data?.topOzLeaks || [];
  const insights = data?.insights || [];
  const recent = data?.recentSettlements || [];

  return (
    <PageLayout icon={InsightsHeaderIcon} heading="Insights" searchPlaceholder="" hideSearchBar>
      <InsightsSubNav />

      <div className="cxo-page">
        <div className="cxo-hero">
          <div>
            <p className="cxo-hero-kicker">CXO · Ceramic settlements</p>
            <h2 className="cxo-hero-title">Settlement Leak Dashboard</h2>
            <p className="cxo-hero-sub">
              We buy from sellers and book Pt/Pd/Rh ounces from weight × assay. Partners (BASF,
              Stillwater) then settle returnable ounces and pay us. This view shows where ounces and
              cash diverge — over-recovery, under-recovery, and where value leaks.
            </p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
            <Link to="/insights/underpayment-leak" className="cxo-hero-link">
              Underpayment Leak
            </Link>
            <Link to="/insights/lot-hedge" className="cxo-hero-link">
              Open Lot Settlement
            </Link>
          </div>
        </div>

        {isLoading && !data ? (
          <div className="cxo-loading">Loading settlement intelligence…</div>
        ) : (
          <>
            <div className="cxo-kpi-grid">
              <div className="cxo-kpi">
                <div className="cxo-kpi-label">Settled lots</div>
                <div className="cxo-kpi-value">{exec.settledLots ?? 0}</div>
                <div className="cxo-kpi-hint">{exec.openLots ?? 0} still open</div>
              </div>
              <div className="cxo-kpi">
                <div className="cxo-kpi-label">Our ceramic $</div>
                <div className="cxo-kpi-value">{money(exec.ourCeramicAmount)}</div>
                <div className="cxo-kpi-hint">Booked from invoices</div>
              </div>
              <div className="cxo-kpi">
                <div className="cxo-kpi-label">Partner paid</div>
                <div className="cxo-kpi-value">{money(exec.partnerPaid)}</div>
                <div className="cxo-kpi-hint">Prelim + final payment</div>
              </div>
              <div className="cxo-kpi">
                <div className="cxo-kpi-label">Cash gap</div>
                <div className={`cxo-kpi-value${varClass(exec.paymentGap)}`}>
                  {moneySigned(exec.paymentGap)}
                </div>
                <div className="cxo-kpi-hint">
                  {(exec.paymentRecoveryPct ?? 0).toFixed(1)}% recovery
                </div>
              </div>
              <div className="cxo-kpi">
                <div className="cxo-kpi-label">Metal value gap</div>
                <div className={`cxo-kpi-value${varClass(exec.metalValueGap)}`}>
                  {moneySigned(exec.metalValueGap)}
                </div>
                <div className="cxo-kpi-hint">Oz var × settlement prices</div>
              </div>
              <div className="cxo-kpi">
                <div className="cxo-kpi-label">Open exposure</div>
                <div className="cxo-kpi-value">{money(exec.openCeramicAmount)}</div>
                <div className="cxo-kpi-hint">Unsettled ceramic amount</div>
              </div>
            </div>

            {insights.length > 0 && (
              <div className="cxo-section">
                <h3 className="cxo-section-title">Executive insights</h3>
                <div className="cxo-insights">
                  {insights.map((text, idx) => (
                    <div key={`${idx}-${text.slice(0, 24)}`} className="cxo-insight">
                      <span className="cxo-insight-mark">{idx + 1}</span>
                      <span>{text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="cxo-section">
              <h3 className="cxo-section-title">Metal recovery vs partner returnable</h3>
              <div className="cxo-metal-grid">
                {["pt", "pd", "rh"].map((key) => {
                  const m = metals[key] || {};
                  const variance = Number(m.ozVariance) || 0;
                  const badgeClass =
                    Math.abs(variance) < 0.00001
                      ? " is-flat"
                      : variance > 0
                        ? " is-pos"
                        : " is-neg";
                  const pct = Number(m.recoveryPct) || 0;
                  return (
                    <div key={key} className="cxo-metal-card">
                      <div className="cxo-metal-head">
                        <span className="cxo-metal-name">{METAL_LABELS[key]}</span>
                        <span className={`cxo-metal-badge${badgeClass}`}>
                          {ozSigned(m.ozVariance)} oz
                        </span>
                      </div>
                      <div className="cxo-metal-rows">
                        <div className="cxo-metal-row">
                          <span>Our oz (assay)</span>
                          <strong>{oz(m.ourOz)}</strong>
                        </div>
                        <div className="cxo-metal-row">
                          <span>Partner returnable</span>
                          <strong>{oz(m.returnableOz)}</strong>
                        </div>
                        <div className="cxo-metal-row">
                          <span>Avg settle price</span>
                          <strong>{money(m.avgPrice)}</strong>
                        </div>
                        <div className="cxo-metal-row">
                          <span>Value gap</span>
                          <strong className={`cxo-var${varClass(m.valueGap)}`}>
                            {moneySigned(m.valueGap)}
                          </strong>
                        </div>
                      </div>
                      <div className="cxo-metal-bar" title={`${pct.toFixed(1)}% recovery`}>
                        <span
                          className={pct >= 100 ? "is-ok" : ""}
                          style={{ width: `${recoveryWidth(pct)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="cxo-two-col">
              <div className="cxo-panel">
                <div className="cxo-panel-head">OEM partner scorecard</div>
                <div className="cxo-panel-body">
                  {byOem.length === 0 ? (
                    <div className="cxo-empty">No OEM settlements yet</div>
                  ) : (
                    <table className="cxo-table">
                      <thead>
                        <tr>
                          <th>OEM</th>
                          <th>Lots</th>
                          <th>Cash gap</th>
                          <th>Oz value gap</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byOem.map((row) => (
                          <tr key={row.oem}>
                            <td>
                              <span className="cxo-oem">{row.oem}</span>
                            </td>
                            <td>{row.settledLots}</td>
                            <td className={`cxo-var${varClass(row.paymentVariance)}`}>
                              {moneySigned(row.paymentVariance)}
                            </td>
                            <td className={`cxo-var${varClass(row.ozValueGap)}`}>
                              {moneySigned(row.ozValueGap)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              <div className="cxo-panel">
                <div className="cxo-panel-head">Leak signals</div>
                <div className="cxo-panel-body">
                  <table className="cxo-table">
                    <tbody>
                      <tr>
                        <td>Lots partner paid less than our ceramic $</td>
                        <td>
                          <strong className="cxo-var is-neg">{exec.lotsPartnerPaidLess ?? 0}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td>Lots partner paid more</td>
                        <td>
                          <strong className="cxo-var is-pos">{exec.lotsPartnerPaidMore ?? 0}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td>Lots with metal oz short (returnable &lt; ours)</td>
                        <td>
                          <strong className="cxo-var is-neg">{exec.lotsOzShort ?? 0}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td>Lots with metal oz long (returnable &gt; ours)</td>
                        <td>
                          <strong className="cxo-var is-pos">{exec.lotsOzLong ?? 0}</strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="cxo-two-col">
              <div className="cxo-panel">
                <div className="cxo-panel-head">Top cash under-recoveries</div>
                <div className="cxo-panel-body">
                  {topPaymentLeaks.length === 0 ? (
                    <div className="cxo-empty">No settled lots to rank</div>
                  ) : (
                    <table className="cxo-table">
                      <thead>
                        <tr>
                          <th>Lot</th>
                          <th>OEM</th>
                          <th>Ours</th>
                          <th>Partner</th>
                          <th>Gap</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topPaymentLeaks.map((row) => (
                          <tr key={`pay-${row.lotName}-${row.oem}`}>
                            <td className="cxo-lot">{row.lotName || "—"}</td>
                            <td>
                              <span className="cxo-oem">{row.oem}</span>
                            </td>
                            <td>{money(row.ourCeramicAmount)}</td>
                            <td>{money(row.partnerPaid)}</td>
                            <td className={`cxo-var${varClass(row.paymentVariance)}`}>
                              {moneySigned(row.paymentVariance)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              <div className="cxo-panel">
                <div className="cxo-panel-head">Top metal ounce / value leaks</div>
                <div className="cxo-panel-body">
                  {topOzLeaks.length === 0 ? (
                    <div className="cxo-empty">No ounce variances yet</div>
                  ) : (
                    <table className="cxo-table">
                      <thead>
                        <tr>
                          <th>Lot</th>
                          <th>Metal</th>
                          <th>Ours</th>
                          <th>Returnable</th>
                          <th>Value gap</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topOzLeaks.map((row, idx) => (
                          <tr key={`oz-${row.lotName}-${row.metal}-${idx}`}>
                            <td className="cxo-lot">{row.lotName || "—"}</td>
                            <td>{row.metal}</td>
                            <td>{oz(row.ourOz)}</td>
                            <td>{oz(row.returnableOz)}</td>
                            <td className={`cxo-var${varClass(row.valueGap)}`}>
                              {moneySigned(row.valueGap)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>

            <div className="cxo-panel">
              <div className="cxo-panel-head">Recent settlements</div>
              <div className="cxo-panel-body">
                {recent.length === 0 ? (
                  <div className="cxo-empty">No settlements created yet</div>
                ) : (
                  <table className="cxo-table">
                    <thead>
                      <tr>
                        <th>Lot</th>
                        <th>OEM</th>
                        <th>Pt var</th>
                        <th>Pd var</th>
                        <th>Rh var</th>
                        <th>$ var</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recent.map((row) => (
                        <tr key={`recent-${row.lotName}-${row.createdDate}`}>
                          <td className="cxo-lot">{row.lotName || "—"}</td>
                          <td>
                            <span className="cxo-oem">{row.oem}</span>
                          </td>
                          <td className={`cxo-var${varClass(row.ptOzVariance)}`}>
                            {ozSigned(row.ptOzVariance)}
                          </td>
                          <td className={`cxo-var${varClass(row.pdOzVariance)}`}>
                            {ozSigned(row.pdOzVariance)}
                          </td>
                          <td className={`cxo-var${varClass(row.rhOzVariance)}`}>
                            {ozSigned(row.rhOzVariance)}
                          </td>
                          <td className={`cxo-var${varClass(row.paymentVariance)}`}>
                            {moneySigned(row.paymentVariance)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {toastParams.message && (
        <Toast type={toastParams.type} message={toastParams.message} id={toastParams.id} />
      )}
    </PageLayout>
  );
};

export default CxoSettlementDashboard;
